import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { ConfirmationService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { BaseServices } from 'src/app/kuba/kuba.services';
import { FlexiComplianceTemplateService } from './flexi-compliance-template.service';
import { FlexiComplianceTemplateSummary } from './flexi-compliance-template.models';

/**
 * Template-author landing page for the Flexi Compliance template library.
 * Appraisal-templates-style table card: count badge in the header, status
 * pills, stacked name + description per row. Creation goes through the
 * 4-step wizard (templates/new) — the old inline create form is gone.
 * Backend enforces template-author auth; the FE surfaces 401/403 as a
 * clean access-denied state for users who navigate here directly.
 */
@Component({
  selector: 'flexi-compliance-templates-list',
  templateUrl: './templates-list.component.html',
  styleUrls: ['./templates.scss'],
  standalone: false
})
export class TemplatesListComponent implements OnInit {
  templates: FlexiComplianceTemplateSummary[] = [];
  loading = false;
  loadError: string | null = null;

  /**
   * True once the server has rejected the list call with 403. Visibility
   * gating is server-driven (TemplateAuthorPolicy: Admin / any HSE
   * advisor); the FE just renders the page and surfaces the rejection so
   * regular Editor / Guest / User who navigate directly see a clean
   * "access denied" instead of a blank page.
   */
  accessDenied = false;

  /** Toggle: include soft-deleted templates in the list. Off by default. */
  includeDeleted = false;

  /** Per-row busy latch so delete buttons can disable individually. */
  busyIds = new Set<number>();

  constructor(
    private service: FlexiComplianceTemplateService,
    private router: Router,
    private route: ActivatedRoute,
    private confirm: ConfirmationService,
    private translate: TranslateService
  ) {}

  /**
   * The page is only ever reached from a business's Flexi compliance tab, so
   * the library it shows is that business's: its own templates plus unclaimed
   * ones. Without the scope it listed every customer's named template.
   */
  get businessId(): number { return BaseServices.BusinessId; }

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading = true;
    this.loadError = null;
    this.accessDenied = false;
    this.service.list(this.includeDeleted, this.businessId).subscribe({
      next: rows => {
        this.templates = rows;
        this.loading = false;
      },
      error: (err: HttpErrorResponse) => {
        this.loading = false;
        if (err?.status === 401 || err?.status === 403) {
          this.accessDenied = true;
          return;
        }
        this.loadError = err?.error?.detail
          ?? this.translate.instant('FLEXI_COMPLIANCE_TEMPLATE.ERRORS.LIST_FAILED');
      }
    });
  }

  onToggleDeleted(): void {
    this.load();
  }

  openWizard(): void {
    this.router.navigate(['new'], { relativeTo: this.route });
  }

  edit(t: FlexiComplianceTemplateSummary): void {
    this.router.navigate([t.id], { relativeTo: this.route });
  }

  remove(t: FlexiComplianceTemplateSummary): void {
    this.confirm.confirm({
      message: this.translate.instant('FLEXI_COMPLIANCE_TEMPLATE.CONFIRM.DELETE_TEMPLATE'),
      accept: () => {
        this.busyIds.add(t.id);
        this.service.remove(t.id).subscribe({
          next: () => {
            this.busyIds.delete(t.id);
            this.load();
          },
          error: (err: HttpErrorResponse) => {
            this.busyIds.delete(t.id);
            this.loadError = err?.error?.detail
              ?? this.translate.instant('FLEXI_COMPLIANCE_TEMPLATE.ERRORS.DELETE_FAILED');
          }
        });
      }
    });
  }

  statusKey(t: FlexiComplianceTemplateSummary): string {
    if (t.isDeleted) return 'FLEXI_COMPLIANCE_TEMPLATE.STATUS.DELETED';
    return t.isActive
      ? 'FLEXI_COMPLIANCE_TEMPLATE.STATUS.ACTIVE'
      : 'FLEXI_COMPLIANCE_TEMPLATE.STATUS.INACTIVE';
  }

  statusClass(t: FlexiComplianceTemplateSummary): string {
    if (t.isDeleted) return 'is-deleted';
    return t.isActive ? 'is-active' : 'is-inactive';
  }
}
